import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import {
    COLORS,
    cardSx,
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceHeroStat,
    SPACE,
    FinanceDetailsSection,
} from "../shared/FinanceUiKit";
import {
    getGrossBilledAmount,
    getVatAmount,
    hasVatIncluded,
} from "../shared/vatDisplay";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { Avatar, Card, Chip, Divider, Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Skeleton, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    Add,
    ArrowDownward,
    ArrowUpward,
    CheckCircle,
    Download,
    DirectionsBoat,
    Edit,
    Flight,
    FilterAlt,
    Inventory2,
    LocalShipping,
    MarkEmailRead,
    Paid,
    PictureAsPdf,
    ReceiptLong,
    Refresh,
    RestartAlt,
    Search,
    Visibility,
    WarningAmber,
} from "@mui/icons-material";

import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatCurrency, formatDate, formatPercent } from "../utils/formatter";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";
import {
    EdmOverview,
    EdmProfileSelect,
    EdmStatusPill,
    EdmStatusSelect,
    getEdmState,
    matchesEdmFilters,
    readEdmFilterFromUrl,
} from "../shared/edmStatus";
// Koyu hero bandı için (yedekten geri yüklendi)
import { WorkspacePremium } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

const getEntityId = (value) => {
    if (!value) return "";

    if (typeof value === "object") {
        return getEntityId(value?._id || value?.id || value?.$oid);
    }

    return String(value).trim();
};

const COLLECTION_RISK_OPTIONS = [
    {
        value: "normal",
        label: "Normal",
        color: "#027A48",
        background: "#ECFDF3",
        border: "#ABEFC6",
    },
    {
        value: "overdue",
        label: "Overdue",
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    {
        value: "doubtful",
        label: "Doubtful",
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    {
        value: "bad_debt",
        label: "Bad Debt",
        color: "#B42318",
        background: "#FEF3F2",
        border: "#FECDCA",
    },
    {
        value: "written_off",
        label: "Written Off",
        color: "#344054",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
];

const normalizeCollectionRiskStatus = (value) => {
    const normalized = String(value || "normal")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");

    const aliases = {
        watch: "overdue",
        high: "doubtful",
        high_risk: "doubtful",
        highrisk: "doubtful",
        legal: "bad_debt",
        write_off: "written_off",
        writeoff: "written_off",
    };

    const resolvedStatus = aliases[normalized] || normalized;

    return COLLECTION_RISK_OPTIONS.some(
        (option) => option.value === resolvedStatus,
    )
        ? resolvedStatus
        : "normal";
};

const getCollectionRisk = (invoice) => {
    const source =
        invoice?.collectionRisk ||
        invoice?.collection_risk ||
        invoice?.invoice?.collectionRisk ||
        invoice?.invoice?.collection_risk ||
        {};

    return {
        status: normalizeCollectionRiskStatus(
            source?.status ||
            invoice?.riskStatus ||
            invoice?.risk_status ||
            invoice?.invoice?.riskStatus ||
            invoice?.invoice?.risk_status,
        ),
        reason: source?.reason || "",
        provisionAmount: toNumber(
            source?.provisionAmount ?? source?.provision_amount,
        ),
        notes: source?.notes || "",
    };
};

const getCollectionRiskStyle = (status) =>
    COLLECTION_RISK_OPTIONS.find(
        (option) => option.value === normalizeCollectionRiskStatus(status),
    ) || COLLECTION_RISK_OPTIONS[0];

const getDocumentType = (invoice) =>
    String(invoice?.documentType || invoice?.document_type || "invoice")
        .trim()
        .toLowerCase() === "return_invoice"
        ? "return_invoice"
        : "invoice";

const isReturnInvoice = (invoice) =>
    getDocumentType(invoice) === "return_invoice";

const getReturnStatus = (invoice) =>
    String(invoice?.returnStatus || invoice?.return_status || "Draft").trim();

const getOriginalInvoiceId = (invoice) =>
    getEntityId(
        invoice?.originalInvoiceId ||
        invoice?.original_invoice_id ||
        invoice?.originalInvoice?._id ||
        invoice?.originalInvoice?.id,
    );

const getOriginalInvoiceNo = (invoice) =>
    invoice?.originalInvoiceNumber ||
    invoice?.original_invoice_number ||
    invoice?.originalInvoiceNo ||
    invoice?.original_invoice_no ||
    invoice?.originalInvoice?.invoiceNo ||
    invoice?.originalInvoice?.invoice_no ||
    "";

const getBuyingRate = (invoice) =>
    firstDefinedNumber(
        invoice?.buyingRate,
        invoice?.buying_rate,
        invoice?.financialSummary?.buyingRate,
        invoice?.financial_summary?.buying_rate,
        invoice?.freightCharges?.buyingRate,
        invoice?.freight_charges?.buying_rate,
    );

const getOperationalCost = (invoice) => {
    if (isReturnInvoice(invoice)) return 0;

    const summary = invoice?.financialSummary || invoice?.financial_summary || {};

    const freightCharges =
        invoice?.freightCharges || invoice?.freight_charges || {};

    const explicitOperationalCost = firstDefinedNumber(
        invoice?.operationalCost,
        invoice?.operational_cost,
        summary?.operationalCost,
        summary?.operational_cost,
    );

    if (explicitOperationalCost > 0) {
        return explicitOperationalCost;
    }

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    if (freightChargeCost > 0) return freightChargeCost;

    return 0;
};

const getTotalCost = (invoice) => {
    if (isReturnInvoice(invoice)) return 0;

    const explicitTotalCost = firstDefinedNumber(
        invoice?.actualCostTotal,
        invoice?.actual_cost_total,
        invoice?.totalCost,
        invoice?.total_cost,
        invoice?.financialSummary?.totalCost,
        invoice?.financial_summary?.total_cost,
    );

    const invoiceItems =
        [invoice?.invoiceItems, invoice?.invoice_items].find(Array.isArray) || [];
    const invoiceItemCost = invoiceItems.length
        ? invoiceItems.reduce(
            (sum, item) =>
                sum +
                firstDefinedNumber(
                    item?.totalCost,
                    item?.total_cost,
                    item?.cost,
                    item?.unitCost,
                    item?.unit_cost,
                    item?.buyingRate,
                ),
            0,
        )
        : 0;

    if (explicitTotalCost > 0) {
        // Backend tarafından hesaplanmış toplam cost nihai değerdir.
        // Aynı buying rate'i ikinci kez eklemek cost ve profit'i şişirir.
        return explicitTotalCost;
    }

    if (invoiceItemCost > 0) return invoiceItemCost;

    return getOperationalCost(invoice) + getBuyingRate(invoice);
};

const getFinanceJobKey = (invoice) =>
    getEntityId(
        invoice?.financeJobId ||
        invoice?.finance_job_id ||
        invoice?.financeJob?._id ||
        invoice?.financeJob?.id ||
        invoice?.jobId ||
        invoice?.job_id ||
        invoice?.shipmentId ||
        invoice?.shipment_id ||
        invoice?.shipment?._id ||
        invoice?.shipment?.id,
    ) ||
    `invoice:${
        getEntityId(invoice?._id || invoice?.id) ||
        invoice?.invoiceNo ||
        invoice?.invoice_no ||
        invoice?.documentNo ||
        invoice?.document_no ||
        "unknown"
    }`;

const getProfit = (invoice) =>
    getInvoiceAmount(invoice) - getTotalCost(invoice);

const getMargin = (invoice) => {
    const revenue = getInvoiceAmount(invoice);
    return revenue > 0 ? (getProfit(invoice) / revenue) * 100 : 0;
};

const getRawInvoiceAmount = (invoice) =>
    Number(
        isReturnInvoice(invoice)
            ? invoice?.returnedAmount ||
            invoice?.returned_amount ||
            invoice?.totalAmount ||
            invoice?.total_amount ||
            invoice?.invoiceSalesAmount ||
            invoice?.amount ||
            0
            : invoice?.grossInvoiceSalesAmount ||
            invoice?.gross_invoice_sales_amount ||
            invoice?.originalAmount ||
            invoice?.original_amount ||
            invoice?.invoiceSalesAmount ||
            invoice?.salesAmount ||
            invoice?.customerInvoiceAmount ||
            invoice?.totalAmount ||
            invoice?.total_amount ||
            invoice?.amount ||
            0,
    );

const getInvoiceAmount = (invoice) => {
    const amount = Math.abs(getRawInvoiceAmount(invoice));

    if (!isReturnInvoice(invoice)) return amount;
    if (getReturnStatus(invoice).toLowerCase() !== "issued") return 0;

    return -amount;
};

const getDisplayInvoiceAmount = (invoice) =>
    isReturnInvoice(invoice)
        ? -Math.abs(getRawInvoiceAmount(invoice))
        : Math.abs(getRawInvoiceAmount(invoice));

const getPaidAmount = (invoice) =>
    isReturnInvoice(invoice)
        ? 0
        : Number(
            invoice?.paidAmount ||
            invoice?.paid_amount ||
            invoice?.collectedAmount ||
            0,
        );

const getBalanceAmount = (invoice) =>
    isReturnInvoice(invoice)
        ? 0
        : Number(
            invoice?.balanceAmount ||
            invoice?.balance_amount ||
            Math.max(getInvoiceAmount(invoice) - getPaidAmount(invoice), 0),
        );

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();
    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";
    return code;
};

// Currencies offered by the "Display Currency" selector -- the currency every
// AGGREGATED figure on this page (KPI cards, hero stats, transport-mode
// cards, charts and the CSV export's converted columns) is blended into.
// Deliberately separate from `currencyFilter`, which only narrows WHICH
// records are listed by their own native currency.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

const getCurrency = (invoice) =>
    normalizeCurrencyCode(
        invoice?.sellingRateCurrency ||
        invoice?.selling_rate_currency ||
        invoice?.currency ||
        invoice?.invoiceCurrency ||
        invoice?.invoice_currency ||
        invoice?.financialSummary?.sellingRateCurrency ||
        invoice?.financialSummary?.totalCurrency ||
        invoice?.financial_summary?.total_currency ||
        invoice?.freightCharges?.totalCurrency ||
        invoice?.freight_charges?.total_currency ||
        "EUR",
    );

const getCostCurrency = (invoice) => {
    const items =
        [
            invoice?.invoiceItems,
            invoice?.invoice_items,
            invoice?.cargoDetails,
            invoice?.cargo_details,
        ].find(Array.isArray) || [];
    const itemWithCurrency = items.find(
        (item) => item?.costCurrency || item?.cost_currency,
    );
    const itemCurrency =
        itemWithCurrency?.costCurrency || itemWithCurrency?.cost_currency;

    return normalizeCurrencyCode(
        invoice?.costCurrency ||
        invoice?.cost_currency ||
        invoice?.totalCostCurrency ||
        invoice?.total_cost_currency ||
        itemCurrency ||
        invoice?.financialSummary?.costCurrency ||
        invoice?.financial_summary?.cost_currency ||
        invoice?.financialSummary?.totalCurrency ||
        invoice?.financial_summary?.total_currency ||
        "EUR",
    );
};

const getShipmentNo = (invoice) =>
    getValue(invoice, "jobNo", "job_no") ||
    invoice?.shipmentNo ||
    invoice?.shipment_no ||
    invoice?.invoiceNo ||
    invoice?.invoice_no ||
    "N/A";

const getCustomerName = (invoice) =>
    getValue(
        invoice,
        "customerName",
        "customer_name",
        invoice?.customer || "N/A",
    );

const getSupplierName = (invoice) =>
    invoice?.supplierName ||
    invoice?.supplier_name ||
    invoice?.serviceProviderName ||
    invoice?.service_provider_name ||
    invoice?.linehaulServiceProvider ||
    invoice?.supplier?.companyName ||
    invoice?.serviceProvider?.companyName ||
    "N/A";

const getDateValue = (invoice) => {
    const raw =
        invoice?.invoiceDate ||
        invoice?.invoice_date ||
        invoice?.createdAt ||
        invoice?.created_at ||
        invoice?.updatedAt ||
        invoice?.updated_at;

    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const normalizeCountry = (value) =>
    String(value || "")
        .trim()
        .toLocaleUpperCase("tr-TR")
        .replace(/İ/g, "I")
        .replace(/Ü/g, "U");
const normalizeLookupKey = (value) => {
    if (!value) return "";
    if (typeof value === "object")
        return normalizeLookupKey(
            value?._id ||
            value?.id ||
            value?.$oid ||
            value?.nameKey ||
            value?.name ||
            value?.companyName,
        );
    return String(value)
        .trim()
        .toLocaleUpperCase("tr-TR")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
};
const getDirectCountries = (item) =>
    [
        item,
        item?.customer,
        item?.serviceProvider,
        item?.supplier,
        item?.globalAgent,
        item?.originalInvoice,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .filter((value) => value && typeof value === "object")
        .flatMap((value) => [
            value?.customerCountry,
            value?.serviceProviderCountry,
            value?.globalAgentCountry,
            ...(Array.isArray(value?.servicesCountry) ? value.servicesCountry : []),
        ])
        .filter(Boolean);
const getPartyKeys = (item) =>
    [
        item?.customerId,
        item?.customer,
        item?.customerName,
        item?.customer_name,
        item?.serviceProviderId,
        item?.serviceProvider,
        item?.serviceProviderName,
        item?.supplierId,
        item?.supplier,
        item?.supplierName,
        item?.supplier_name,
        item?.globalAgentId,
        item?.globalAgent,
        item?.globalAgentName,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
        item?.originalInvoice?.customerId,
        item?.originalInvoice?.customerName,
        item?.originalInvoice?.supplierId,
        item?.originalInvoice?.supplierName,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);
const getGeographicScope = (item, lookup = new Map()) => {
    const countries = [
        ...getDirectCountries(item),
        ...getPartyKeys(item).flatMap((key) => lookup.get(key) || []),
    ];
    return countries.some((country) =>
        ["TURKEY", "TURKIYE"].includes(normalizeCountry(country)),
    )
        ? "DOMESTIC"
        : "INTERNATIONAL";
};
const getGeographicLabel = (item, lookup) =>
    getGeographicScope(item, lookup) === "DOMESTIC"
        ? "Domestic"
        : "International";

const getInvoiceNo = (invoice) =>
    getValue(invoice, "invoiceNo", "invoice_no") ||
    invoice?.edm?.invoiceId ||
    invoice?._id ||
    "Draft";

const getOverdueDays = (dueDate, collectionStatus) => {
    if (!dueDate) return 0;

    const status = String(collectionStatus || "").toLowerCase();

    if (
        status.includes("paid") ||
        status.includes("collected") ||
        status.includes("closed")
    ) {
        return 0;
    }

    const today = new Date();
    const due = new Date(dueDate);

    if (Number.isNaN(due.getTime())) return 0;

    // Whole calendar days past the due date: an invoice due today is not
    // overdue yet, and the time of day must not round a partial day up.
    const startOfDay = (value) =>
        new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const days = Math.round(
        (startOfDay(today).getTime() - startOfDay(due).getTime()) / 86400000,
    );

    return days > 0 ? days : 0;
};

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(value || 0));

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    }).format(Number(value || 0));

const normalizeText = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[ıİ]/g, "i");

const getLinkedReturnInvoices = (invoice) => {
    const candidates = [
        invoice?.returnInvoices,
        invoice?.return_invoices,
        invoice?.linkedReturnInvoices,
        invoice?.linked_return_invoices,
    ];

    return candidates.find(Array.isArray) || [];
};

const getReturnDate = (invoice) => {
    const date = getDateValue(invoice);
    return date ? date.getTime() : 0;
};

const getReturnSummary = (invoice) => {
    if (isReturnInvoice(invoice)) {
        return {
            status: getReturnStatus(invoice),
            amount: Math.abs(getRawInvoiceAmount(invoice)),
            count: 1,
            latestReturnInvoice: invoice,
        };
    }

    const allReturns = getLinkedReturnInvoices(invoice);
    const activeReturns = allReturns.filter(
        (item) => normalizeText(getReturnStatus(item)) !== "cancelled",
    );
    const issuedReturns = activeReturns.filter(
        (item) => normalizeText(getReturnStatus(item)) === "issued",
    );
    const latestActiveReturn =
        [...activeReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const latestReturnInvoice =
        [...allReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const issuedAmount = issuedReturns.reduce(
        (sum, item) => sum + Math.abs(getRawInvoiceAmount(item)),
        0,
    );
    const activeAmount = activeReturns.reduce(
        (sum, item) => sum + Math.abs(getRawInvoiceAmount(item)),
        0,
    );
    const originalAmount = Math.abs(getRawInvoiceAmount(invoice));
    const latestStatus = normalizeText(getReturnStatus(latestActiveReturn));

    let status = "No Return";
    if (latestStatus === "draft") status = "Return Draft";
    else if (latestStatus === "approved") status = "Return Approved";
    else if (
        issuedAmount > 0 &&
        originalAmount > 0 &&
        issuedAmount >= originalAmount - 0.01
    ) {
        status = "Full Return";
    } else if (issuedAmount > 0) status = "Partial Return";
    else if (activeAmount > 0) status = "Return Pending";
    else if (allReturns.length > 0) status = "Return Cancelled";

    return {
        status,
        amount: activeAmount,
        count: allReturns.length,
        latestReturnInvoice,
    };
};

const RETURN_STATUS_STYLES = {
    "No Return": { color: "#475467", background: "#F2F4F7", border: "#D0D5DD" },
    Draft: { color: "#B54708", background: "#FFFAEB", border: "#FEDF89" },
    "Return Draft": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    Approved: { color: "#175CD3", background: "#EFF8FF", border: "#B2DDFF" },
    "Return Approved": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    Issued: { color: "#027A48", background: "#ECFDF3", border: "#ABEFC6" },
    "Partial Return": {
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    "Full Return": { color: "#B42318", background: "#FEF3F2", border: "#FECDCA" },
    "Return Pending": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    Cancelled: { color: "#475467", background: "#F2F4F7", border: "#D0D5DD" },
    "Return Cancelled": {
        color: "#475467",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
};

const getDocumentStatus = (invoice) =>
    isReturnInvoice(invoice)
        ? getReturnStatus(invoice)
        : String(
            getValue(invoice, "invoiceStatus", "invoice_status", "Issued"),
        ).trim();

const getCollectionStatus = (invoice) =>
    isReturnInvoice(invoice)
        ? "Not Applicable"
        : String(
            getValue(invoice, "collectionStatus", "collection_status", "Pending"),
        ).trim();

const getInvoiceOrigin = (invoice) =>
    normalizeText(
        invoice?.invoiceOrigin ||
        invoice?.invoice_origin ||
        invoice?.originalInvoice?.invoiceOrigin ||
        invoice?.originalInvoice?.invoice_origin ||
        "tms",
    ) === "manual"
        ? "manual"
        : "tms";

const getTransportMode = (invoice) => {
    if (getInvoiceOrigin(invoice) === "manual") {
        return "manual";
    }

    const candidates = [
        invoice?.sourceType,
        invoice?.source_type,
        invoice?.transportMode,
        invoice?.transport_mode,
        invoice?.shipmentMode,
        invoice?.shipment_mode,
        invoice?.productType,
        invoice?.product_type,
        invoice?.serviceType,
        invoice?.service_type,
        invoice?.tmsSource,
        invoice?.tms_source,
        invoice?.module,
        invoice?.source,
        invoice?.originalInvoice?.sourceType,
        invoice?.originalInvoice?.transportMode,
        invoice?.originalInvoice?.productType,
        invoice?.originalInvoice?.serviceType,
        invoice?.originalInvoice?.source,
        getShipmentNo(invoice),
    ]
        .map(normalizeText)
        .join(" ");

    if (
        candidates.includes("ocean") ||
        candidates.includes("sea") ||
        candidates.includes("fcl") ||
        candidates.includes("lcl") ||
        candidates.includes("ohfl") ||
        candidates.includes("ombl")
    ) {
        return "ocean";
    }

    if (
        candidates.includes("air") ||
        candidates.includes("flight") ||
        candidates.includes("ahfl") ||
        candidates.includes("ambl")
    ) {
        return "air";
    }

    return "road";
};

const getShipmentType = (invoice) => {
    const raw =
        invoice?.shipmentType ||
        invoice?.shipment_type ||
        invoice?.transportType ||
        invoice?.transport_type ||
        invoice?.oceanTransportType ||
        invoice?.ocean_transport_type ||
        invoice?.airTransportType ||
        invoice?.air_transport_type ||
        invoice?.serviceType ||
        invoice?.service_type ||
        invoice?.originalInvoice?.shipmentType ||
        invoice?.originalInvoice?.transportType ||
        invoice?.originalInvoice?.oceanTransportType ||
        invoice?.originalInvoice?.airTransportType ||
        invoice?.originalInvoice?.serviceType ||
        "General";

    const normalized = String(raw).trim();
    return normalized || "General";
};

const MODE_CONFIG = {
    ocean: {
        label: "Ocean",
        color: "#1570EF",
        light: "#EFF8FF",
        border: "#B2DDFF",
        icon: DirectionsBoat,
    },
    road: {
        label: "Road",
        color: "#B54708",
        light: "#FFFAEB",
        border: "#FEDF89",
        icon: LocalShipping,
    },
    air: {
        label: "Air",
        color: "#6941C6",
        light: "#F4F3FF",
        border: "#D9D6FE",
        icon: Flight,
    },
    manual: {
        label: "Manual",
        color: "#344054",
        light: "#F2F4F7",
        border: "#D0D5DD",
        icon: ReceiptLong,
    },
    other: {
        label: "Other",
        color: "#475467",
        light: "#F9FAFB",
        border: "#D0D5DD",
        icon: ReceiptLong,
    },
};

const TransportInvoiceCard = ({
                                  mode,
                                  data,
                                  totalInvoices,
                                  currency = "EUR",
                              }) => {
    const config = MODE_CONFIG[mode] || MODE_CONFIG.other;
    const safeData = data || {
        invoices: 0,
        amount: 0,
        paid: 0,
        balance: 0,
        profit: 0,
        margin: 0,
        collectionRate: 0,
    };
    const Icon = config.icon;
    const share = totalInvoices ? (safeData.invoices / totalInvoices) * 100 : 0;

    return (
        <Card sx={{ ...cardSx, height: "100%" }}>
            <MDBox p={2.4}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <MDBox>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: COLORS.subtext, letterSpacing: ".08em" }}
                        >
                            {`${financeText(config.label).toUpperCase()} ${financeText("INVOICES")}`}
                        </MDTypography>
                        <MDTypography
                            variant="h4"
                            fontWeight="bold"
                            mt={0.8}
                            sx={{ color: COLORS.text }}
                        >
                            {formatNumber(safeData.invoices)}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Customer invoices")} · {share.toFixed(1)}% {financeText("share")}
                        </MDTypography>
                    </MDBox>

                    <MDBox
                        width={46}
                        height={46}
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            color: config.color,
                            background: config.light,
                            border: `1px solid ${config.border}`,
                        }}
                    >
                        <Icon />
                    </MDBox>
                </MDBox>

                <MDBox mt={2}>
                    <MDBox display="flex" justifyContent="space-between" mb={0.6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Collection rate")}
                        </MDTypography>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: config.color }}
                        >
                            {formatPercent(safeData.collectionRate)}
                        </MDTypography>
                    </MDBox>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, safeData.collectionRate))}
                        sx={{
                            height: 7,
                            borderRadius: "999px",
                            backgroundColor: "#EEF2F6",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: "999px",
                                backgroundColor: config.color,
                            },
                        }}
                    />
                </MDBox>

                <Grid container spacing={1.2} mt={0.8}>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >
                            {financeText("Billed")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: config.color }}
                        >
                            {formatCompact(safeData.amount, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >
                            {financeText("Collected")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.green }}
                        >
                            {formatCompact(safeData.paid, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >
                            {financeText("Outstanding")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: safeData.balance > 0 ? COLORS.red : COLORS.green }}
                        >
                            {formatCompact(safeData.balance, currency)}
                        </MDTypography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                        {financeText("Gross profit")}
                    </MDTypography>
                    <MDTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{ color: safeData.profit >= 0 ? COLORS.green : COLORS.red }}
                    >
                        {formatCompact(safeData.profit, currency)} ·{" "}
                        {formatPercent(safeData.margin)}
                    </MDTypography>
                </MDBox>
            </MDBox>
        </Card>
    );
};

const ModeReceivablesChart = ({ summary, currency = "EUR" }) => {
    const maxAmount = Math.max(
        ...Object.values(summary).map((item) => item.amount),
        1,
    );

    return (
        <MDBox>
            {Object.entries(summary).map(([mode, item]) => {
                const config = MODE_CONFIG[mode] || MODE_CONFIG.other;
                const width = (item.amount / maxAmount) * 100;

                return (
                    <MDBox key={mode} mb={2.2}>
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.8}
                        >
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDBox
                                    width={10}
                                    height={10}
                                    borderRadius="50%"
                                    sx={{ background: config.color }}
                                />
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText(config.label)}
                                </MDTypography>
                            </MDBox>
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    sx={{ color: config.color }}
                                >
                                    {formatCompact(item.amount, currency)}
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                    {financeText("Outstanding")}{" "}
                                    {formatCompact(item.balance, currency)}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                        <MDBox
                            height={12}
                            borderRadius="999px"
                            sx={{ background: "#EEF2F6", overflow: "hidden" }}
                        >
                            <MDBox
                                height="100%"
                                borderRadius="999px"
                                sx={{
                                    width: `${Math.max(width, item.invoices ? 4 : 0)}%`,
                                    background: config.color,
                                }}
                            />
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
};

const CustomerInvoices = () => {
    const navigate = useNavigate();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [invoices, setInvoices] = useState([]);
    const [businessPartners, setBusinessPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const location = useLocation();
    // EDM filtresi Finance Dashboard bağlantısından (?edm=notSent) önceden seçili gelebilir
    const [edmFilter, setEdmFilter] = useState(() => readEdmFilterFromUrl(location.search));
    const [edmProfile, setEdmProfile] = useState("all");
    const [search, setSearch] = useState("");
    const [invoiceStatus, setInvoiceStatus] = useState("all");
    const [collectionStatus, setCollectionStatus] = useState("all");
    const [creditRisk, setCreditRisk] = useState("all");
    const [dueStatus, setDueStatus] = useState("all");
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const todayValue = `${currentYear}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(todayValue);
    const [selectedParty, setSelectedParty] = useState(null);
    const [transportMode, setTransportMode] = useState("all");
    const [currencyFilter, setCurrencyFilter] = useState("all");
    const [geographicScope, setGeographicScope] = useState("all");
    const [invoiceOrigin, setInvoiceOrigin] = useState("all");
    const [documentType, setDocumentType] = useState("all");
    const [returnStatus, setReturnStatus] = useState("all");
    const [exchangeRates, setExchangeRates] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    const token = localStorage.getItem("token");
    // The currency every AGGREGATED/BLENDED figure below is converted into
    // before it is displayed. It now follows the user's explicit "Display
    // Currency" choice instead of being derived from `currencyFilter`:
    // `currencyFilter` only decides WHICH records are listed (by each
    // record's own native currency) and must not silently re-label the
    // blended totals -- e.g. filtering to USD invoices previously forced
    // every KPI card into USD with no way to read them back in EUR.
    const reportingCurrency = normalizeCurrencyCode(selectedCurrency);

    const fetchWithAuth = useCallback(
        async (path) => {
            const response = await fetch(`${API_URL}${path}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`${path} fetch failed`);
            }

            const data = await response.json();
            const candidates = [
                data,
                data?.data,
                data?.invoices,
                data?.returnInvoices,
                data?.return_invoices,
                data?.jobs,
                data?.results,
                data?.docs,
                data?.items,
                data?.records,
                data?.data?.data,
                data?.data?.invoices,
                data?.data?.returnInvoices,
                data?.data?.return_invoices,
                data?.data?.jobs,
                data?.data?.results,
                data?.data?.records,
            ];

            return candidates.find(Array.isArray) || [];
        },
        [token],
    );

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rate fetch error:", error);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = Number(amount) || 0;
            const from = normalizeCurrencyCode(fromCurrency);
            const to = normalizeCurrencyCode(toCurrency);

            if (from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    const getConvertedFinancials = useCallback(
        (invoice, targetCurrency = reportingCurrency) => {
            const amount = convertCurrency(
                getInvoiceAmount(invoice),
                getCurrency(invoice),
                targetCurrency,
            );
            const cost = convertCurrency(
                getTotalCost(invoice),
                getCostCurrency(invoice),
                targetCurrency,
            );

            if (amount === null || cost === null) {
                return { amount: null, cost: null, profit: null, margin: null };
            }

            const profit = amount - cost;
            return {
                amount,
                cost,
                profit,
                margin: amount > 0 ? (profit / amount) * 100 : 0,
            };
        },
        [convertCurrency, reportingCurrency],
    );

    const fetchInvoices = useCallback(async () => {
        try {
            setLoading(true);

            const [financeData, manualData, returnData] = await Promise.all([
                fetchWithAuth("/finance/customer-invoices"),
                fetchWithAuth(
                    "/finance/manual-invoices?invoiceType=sales&limit=200",
                ).catch((error) => {
                    console.warn("Manual invoices fetch failed", error);
                    return [];
                }),
                fetchWithAuth("/finance/return-invoices?invoiceType=sales").catch(
                    (error) => {
                        console.warn("Return invoices fetch failed", error);
                        return [];
                    },
                ),
            ]);

            const originalInvoiceMap = new Map();

            // Manual records are inserted first. If the same document also exists
            // in /finance/customer-invoices, the richer finance payload wins.
            [...manualData, ...financeData]
                .filter(
                    (invoice) =>
                        !isReturnInvoice(invoice) &&
                        (!invoice?.invoiceType ||
                            String(invoice.invoiceType).toLowerCase() === "sales"),
                )
                .forEach((invoice) => {
                    const key = getEntityId(
                        invoice?._id ||
                        invoice?.id ||
                        invoice?.invoiceId ||
                        invoice?.invoice_id ||
                        invoice?.invoiceNo ||
                        `${getInvoiceOrigin(invoice)}-${originalInvoiceMap.size}`,
                    );

                    const previous = originalInvoiceMap.get(key) || {};
                    originalInvoiceMap.set(key, { ...previous, ...invoice });
                });

            const uniqueReturns = Array.from(
                new Map(
                    returnData.map((returnInvoice, index) => [
                        getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                        `return-${index}`,
                        returnInvoice,
                    ]),
                ).values(),
            );
            const returnsByOriginalId = new Map();

            uniqueReturns.forEach((returnInvoice) => {
                const originalId = getOriginalInvoiceId(returnInvoice);
                if (!originalId) return;

                returnsByOriginalId.set(originalId, [
                    ...(returnsByOriginalId.get(originalId) || []),
                    returnInvoice,
                ]);
            });

            const originalInvoices = Array.from(originalInvoiceMap.entries()).flatMap(
                ([invoiceId, invoice]) => {
                    const linkedReturns = [
                        ...getLinkedReturnInvoices(invoice),
                        ...(returnsByOriginalId.get(invoiceId) || []),
                    ];
                    const uniqueLinkedReturns = Array.from(
                        new Map(
                            linkedReturns.map((returnInvoice, index) => [
                                getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                                `${invoiceId}-return-${index}`,
                                returnInvoice,
                            ]),
                        ).values(),
                    );

                    // /finance/customer-invoices groups every invoice for a
                    // shipment into one row keyed by financeJobId, with the
                    // individual invoices nested under customerInvoices and
                    // only pre-summed totals at the top level. When a job
                    // has more than one invoice, that collapsed the second
                    // (and later) invoices out of the table entirely — they
                    // had no row of their own to search, open or record a
                    // payment against. Expand each into its own row instead;
                    // a single-invoice job still renders exactly one row.
                    if (
                        Array.isArray(invoice.customerInvoices) &&
                        invoice.customerInvoices.length > 1
                    ) {
                        return invoice.customerInvoices.map((subInvoice) => ({
                            ...invoice,
                            ...subInvoice,
                            documentType: "invoice",
                            financeJobId: invoice.financeJobId || invoiceId,
                            returnInvoices:
                                subInvoice.returnInvoices?.length
                                    ? subInvoice.returnInvoices
                                    : uniqueLinkedReturns,
                        }));
                    }

                    return [
                        {
                            ...invoice,
                            documentType: "invoice",
                            returnInvoices: uniqueLinkedReturns,
                        },
                    ];
                },
            );

            const enrichedReturnInvoices = uniqueReturns.map((returnInvoice) => {
                const originalInvoice = originalInvoiceMap.get(
                    getOriginalInvoiceId(returnInvoice),
                );

                return {
                    ...returnInvoice,
                    documentType: "return_invoice",
                    originalInvoice: originalInvoice || null,
                    sourceType: returnInvoice?.sourceType || originalInvoice?.sourceType,
                    transportMode:
                        returnInvoice?.transportMode || originalInvoice?.transportMode,
                    productType:
                        returnInvoice?.productType || originalInvoice?.productType,
                    serviceType:
                        returnInvoice?.serviceType || originalInvoice?.serviceType,
                    shipmentType:
                        returnInvoice?.shipmentType || originalInvoice?.shipmentType,
                };
            });

            setInvoices([...originalInvoices, ...enrichedReturnInvoices]);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Customer invoices fetch error:", error);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchBusinessPartners = useCallback(async () => {
        const firstAvailable = async (paths) => {
            for (const path of paths) {
                try {
                    return await fetchWithAuth(path);
                } catch (error) {
                    console.warn(`${path} lookup failed`, error);
                }
            }
            return [];
        };
        const [customers, providers, agents] = await Promise.all([
            firstAvailable(["/customer", "/customers"]),
            firstAvailable(["/serviceProvider", "/service-provider"]),
            firstAvailable(["/globalAgent", "/global-agent"]),
        ]);
        setBusinessPartners([...customers, ...providers, ...agents]);
    }, [fetchWithAuth]);

    useEffect(() => {
        fetchInvoices();
        fetchBusinessPartners();
    }, [fetchInvoices, fetchBusinessPartners]);

    const partyCountryLookup = useMemo(() => {
        const lookup = new Map();
        businessPartners.forEach((record) => {
            const item = record?.attributes || record;
            const countries = [
                item?.customerCountry,
                item?.serviceProviderCountry,
                item?.globalAgentCountry,
                ...(Array.isArray(item?.servicesCountry) ? item.servicesCountry : []),
            ].filter(Boolean);
            [
                record?.id,
                item?._id,
                item?.id,
                item?.name,
                item?.nameKey,
                item?.companyName,
                item?.customerNo,
                item?.serviceProviderNo,
                item?.globalAgentNo,
            ]
                .map(normalizeLookupKey)
                .filter(Boolean)
                .forEach((key) =>
                    lookup.set(key, [...(lookup.get(key) || []), ...countries]),
                );
        });
        return lookup;
    }, [businessPartners]);

    const statusOptions = useMemo(() => {
        return {
            invoice: [
                "all",
                ...Array.from(new Set(invoices.map(getDocumentStatus))).filter(Boolean),
            ],
            collection: [
                "all",
                ...Array.from(new Set(invoices.map(getCollectionStatus))).filter(
                    Boolean,
                ),
            ],
        };
    }, [invoices]);

    const returnStatusOptions = useMemo(
        () => [
            "all",
            ...Array.from(
                new Set(invoices.map((invoice) => getReturnSummary(invoice).status)),
            ).filter(Boolean),
        ],
        [invoices],
    );

    const partyOptions = useMemo(() => {
        const unique = new Map();

        invoices.forEach((invoice) => {
            const customerName = getCustomerName(invoice);
            const supplierName = getSupplierName(invoice);

            if (customerName && customerName !== "N/A") {
                unique.set(`CUSTOMER-${customerName}`, {
                    type: "CUSTOMER",
                    name: customerName,
                    label: `Customer - ${customerName}`,
                });
            }

            if (supplierName && supplierName !== "N/A") {
                unique.set(`SUPPLIER-${supplierName}`, {
                    type: "SUPPLIER",
                    name: supplierName,
                    label: `Supplier - ${supplierName}`,
                });
            }
        });

        return Array.from(unique.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [invoices]);

    const transportModeOptions = useMemo(
        () => Array.from(new Set(invoices.map(getTransportMode))).sort(),
        [invoices],
    );

    const currencyOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    invoices.map((invoice) => String(getCurrency(invoice)).toUpperCase()),
                ),
            ).sort(),
        [invoices],
    );

    const baseFilteredInvoices = useMemo(() => {
        const searchText = search.toLowerCase().trim();
        const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const rangeEnd = endDate ? new Date(`${endDate}T23:59:59`) : null;

        return invoices.filter((invoice) => {
            const jobNo = String(getShipmentNo(invoice)).toLowerCase();
            const customer = String(getCustomerName(invoice)).toLowerCase();
            const invoiceNo = String(getInvoiceNo(invoice)).toLowerCase();
            const email = String(invoice?.email || "").toLowerCase();
            const supplier = String(getSupplierName(invoice)).toLowerCase();
            const originalInvoiceNo = String(
                getOriginalInvoiceNo(invoice),
            ).toLowerCase();
            const returnReason = String(
                invoice?.returnReason || invoice?.return_reason || "",
            ).toLowerCase();
            const invoiceDate = getDateValue(invoice);

            const currentInvoiceStatus = getDocumentStatus(invoice).toLowerCase();
            const currentCollectionStatus =
                getCollectionStatus(invoice).toLowerCase();
            const currentCreditRisk = getCollectionRisk(invoice).status;

            const dueDate = getValue(invoice, "dueDate", "due_date");
            const overdueDays = getOverdueDays(dueDate, currentCollectionStatus);

            const matchesSearch =
                !searchText ||
                jobNo.includes(searchText) ||
                customer.includes(searchText) ||
                supplier.includes(searchText) ||
                invoiceNo.includes(searchText) ||
                originalInvoiceNo.includes(searchText) ||
                returnReason.includes(searchText) ||
                email.includes(searchText);

            const matchesStartDate =
                !rangeStart || (invoiceDate && invoiceDate >= rangeStart);
            const matchesEndDate =
                !rangeEnd || (invoiceDate && invoiceDate <= rangeEnd);
            const matchesParty =
                !selectedParty ||
                (selectedParty.type === "CUSTOMER"
                    ? getCustomerName(invoice) === selectedParty.name
                    : getSupplierName(invoice) === selectedParty.name);
            const matchesTransportMode =
                transportMode === "all" || getTransportMode(invoice) === transportMode;
            const matchesCurrency =
                currencyFilter === "all" ||
                String(getCurrency(invoice)).toUpperCase() === currencyFilter;
            const matchesInvoiceOrigin =
                invoiceOrigin === "all" || getInvoiceOrigin(invoice) === invoiceOrigin;
            const matchesDocumentType =
                documentType === "all" || getDocumentType(invoice) === documentType;
            const matchesReturnStatus =
                returnStatus === "all" ||
                normalizeText(getReturnSummary(invoice).status) ===
                normalizeText(returnStatus);
            const matchesGeographicScope =
                geographicScope === "all" ||
                getGeographicScope(invoice, partyCountryLookup) === geographicScope;

            const matchesInvoiceStatus =
                invoiceStatus === "all" ||
                currentInvoiceStatus === invoiceStatus.toLowerCase();

            const matchesCollectionStatus =
                collectionStatus === "all" ||
                currentCollectionStatus === collectionStatus.toLowerCase();

            const matchesCreditRisk =
                creditRisk === "all" || currentCreditRisk === creditRisk;

            const matchesDueStatus =
                dueStatus === "all" ||
                (dueStatus === "overdue" && overdueDays > 0) ||
                (dueStatus === "notDue" && overdueDays === 0 && Boolean(dueDate)) ||
                (dueStatus === "noDueDate" && !dueDate);

            return (
                matchesSearch &&
                matchesStartDate &&
                matchesEndDate &&
                matchesParty &&
                matchesTransportMode &&
                matchesCurrency &&
                matchesInvoiceOrigin &&
                matchesDocumentType &&
                matchesReturnStatus &&
                matchesGeographicScope &&
                matchesInvoiceStatus &&
                matchesCollectionStatus &&
                matchesCreditRisk &&
                matchesDueStatus
            );
        });
    }, [
        invoices,
        search,
        invoiceStatus,
        collectionStatus,
        creditRisk,
        dueStatus,
        startDate,
        endDate,
        selectedParty,
        transportMode,
        currencyFilter,
        invoiceOrigin,
        documentType,
        returnStatus,
        geographicScope,
        partyCountryLookup,
    ]);

    // EDM kartları diğer filtrelere göre sayar; tablo ve özetler EDM seçimini de uygular
    const filteredInvoices = useMemo(
        () => baseFilteredInvoices.filter((invoice) => matchesEdmFilters(invoice, edmFilter, edmProfile)),
        [baseFilteredInvoices, edmFilter, edmProfile],
    );

    const formatEdmAmount = (list) =>
        formatCurrency(
            list.reduce(
                (sum, item) =>
                    sum +
                    (convertCurrency(
                        hasVatIncluded(item) ? getGrossBilledAmount(item, getInvoiceAmount(item)) : getInvoiceAmount(item),
                        getCurrency(item),
                        reportingCurrency,
                    ) ?? 0),
                0,
            ),
            reportingCurrency,
        );

    const stats = useMemo(() => {
        const countedCostJobs = new Set();
        const totalAmount = filteredInvoices.reduce(
            (sum, item) => sum + (getConvertedFinancials(item).amount ?? 0),
            0,
        );

        // What customers are actually billed (VAT included where the invoice was
        // cut with VAT). totalAmount above stays ex-VAT: profit and margin are
        // measured against it.
        const billedTotal = filteredInvoices.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    !isReturnInvoice(item) && hasVatIncluded(item)
                        ? getGrossBilledAmount(item, getInvoiceAmount(item))
                        : getInvoiceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const paidAmount = filteredInvoices.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getPaidAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const balanceAmount = filteredInvoices.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const overdueCount = filteredInvoices.filter((item) => {
            const dueDate = getValue(item, "dueDate", "due_date");
            const status = getValue(item, "collectionStatus", "collection_status");

            return getOverdueDays(dueDate, status) > 0;
        }).length;

        const draftCount = filteredInvoices.filter((item) =>
            String(getValue(item, "invoiceStatus", "invoice_status"))
                .toLowerCase()
                .includes("draft"),
        ).length;

        const collectionRate = billedTotal ? (paidAmount / billedTotal) * 100 : 0;

        const costTotals = filteredInvoices.reduce(
            (totals, item) => {
                if (isReturnInvoice(item)) return totals;

                const jobKey = getFinanceJobKey(item);
                if (countedCostJobs.has(jobKey)) return totals;
                countedCostJobs.add(jobKey);

                totals.totalCost += getConvertedFinancials(item).cost ?? 0;
                totals.operationalCost +=
                    convertCurrency(
                        getOperationalCost(item),
                        getCostCurrency(item),
                        reportingCurrency,
                    ) ?? 0;
                totals.buyingRate +=
                    convertCurrency(
                        getBuyingRate(item),
                        getCostCurrency(item),
                        reportingCurrency,
                    ) ?? 0;

                return totals;
            },
            { totalCost: 0, operationalCost: 0, buyingRate: 0 },
        );

        const { totalCost, operationalCost, buyingRate } = costTotals;

        const highRiskExposure = filteredInvoices.reduce((sum, item) => {
            const status = getCollectionRisk(item).status;

            if (!["doubtful", "bad_debt", "written_off"].includes(status)) {
                return sum;
            }

            return (
                sum +
                (convertCurrency(
                    getBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0)
            );
        }, 0);

        const profit = totalAmount - totalCost;
        const margin = totalAmount ? (profit / totalAmount) * 100 : 0;

        return {
            totalAmount,
            billedTotal,
            paidAmount,
            balanceAmount,
            overdueCount,
            draftCount,
            invoiceCount: filteredInvoices.length,
            collectionRate,
            totalCost,
            operationalCost,
            buyingRate,
            highRiskExposure,
            profit,
            margin,
        };
    }, [
        filteredInvoices,
        getConvertedFinancials,
        convertCurrency,
        reportingCurrency,
    ]);

    const transportSummary = useMemo(() => {
        const initial = {
            ocean: {
                invoices: 0,
                amount: 0,
                paid: 0,
                balance: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                collectionRate: 0,
                overdue: 0,
            },
            road: {
                invoices: 0,
                amount: 0,
                paid: 0,
                balance: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                collectionRate: 0,
                overdue: 0,
            },
            air: {
                invoices: 0,
                amount: 0,
                paid: 0,
                balance: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                collectionRate: 0,
                overdue: 0,
            },
            manual: {
                invoices: 0,
                amount: 0,
                paid: 0,
                balance: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                collectionRate: 0,
                overdue: 0,
            },
            other: {
                invoices: 0,
                amount: 0,
                paid: 0,
                balance: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                collectionRate: 0,
                overdue: 0,
            },
        };

        const countedCostJobs = new Set();

        filteredInvoices.forEach((invoice) => {
            const detectedMode = getTransportMode(invoice);
            const mode = initial[detectedMode] ? detectedMode : "other";
            const target = initial[mode];
            const financials = getConvertedFinancials(invoice);
            const amount = financials.amount ?? 0;
            const jobKey = getFinanceJobKey(invoice);
            const shouldCountCost =
                !isReturnInvoice(invoice) && !countedCostJobs.has(jobKey);
            const cost = shouldCountCost ? (financials.cost ?? 0) : 0;
            if (shouldCountCost) countedCostJobs.add(jobKey);
            const paid =
                convertCurrency(
                    getPaidAmount(invoice),
                    getCurrency(invoice),
                    reportingCurrency,
                ) ?? 0;
            const balance =
                convertCurrency(
                    getBalanceAmount(invoice),
                    getCurrency(invoice),
                    reportingCurrency,
                ) ?? 0;
            const dueDate = getValue(invoice, "dueDate", "due_date");
            const collection = getValue(
                invoice,
                "collectionStatus",
                "collection_status",
            );

            target.invoices += 1;
            target.amount += amount;
            target.paid += paid;
            target.balance += balance;
            target.cost += cost;
            target.profit += amount - cost;
            if (getOverdueDays(dueDate, collection) > 0) target.overdue += 1;
        });

        Object.values(initial).forEach((item) => {
            item.margin = item.amount ? (item.profit / item.amount) * 100 : 0;
            item.collectionRate = item.amount ? (item.paid / item.amount) * 100 : 0;
        });

        return initial;
    }, [
        filteredInvoices,
        getConvertedFinancials,
        convertCurrency,
        reportingCurrency,
    ]);

    const shipmentTypeSummary = useMemo(() => {
        const counts = {};

        filteredInvoices.forEach((invoice) => {
            const type = getShipmentType(invoice);
            counts[type] = (counts[type] || 0) + 1;
        });

        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8);
    }, [filteredInvoices]);

    const openReturnInvoice = (returnInvoice, originalInvoice = null) => {
        const returnInvoiceId = getEntityId(
            returnInvoice?._id || returnInvoice?.id,
        );

        if (!returnInvoiceId) {
            appAlert(financeText("Return Invoice ID not found!"));
            return;
        }

        navigate(`/invoice/finance/return-invoices/${returnInvoiceId}`, {
            state: {
                returnInvoice,
                originalInvoice:
                    originalInvoice || returnInvoice?.originalInvoice || null,
                originalInvoiceId: getOriginalInvoiceId(returnInvoice),
            },
        });
    };

    const openOriginalInvoice = (returnInvoice) => {
        const originalInvoice = returnInvoice?.originalInvoice || null;
        const originalInvoiceId =
            getOriginalInvoiceId(returnInvoice) ||
            getEntityId(originalInvoice?._id || originalInvoice?.id);

        if (!originalInvoiceId) {
            appAlert(financeText("Original Invoice ID not found!"));
            return;
        }

        navigate(`/invoice/finance/jobs/${originalInvoiceId}`, {
            state: {
                job: originalInvoice,
                invoiceId: originalInvoiceId,
                financeJobId: originalInvoiceId,
                fromReturnInvoice: true,
            },
        });
    };

    const openLatestReturnInvoice = (invoice) => {
        const latestReturnInvoice = getReturnSummary(invoice).latestReturnInvoice;

        if (!latestReturnInvoice) {
            appAlert(financeText("No linked return invoice was found."));
            return;
        }

        openReturnInvoice(latestReturnInvoice, invoice);
    };

    const openInvoiceEditor = async (invoice, mode = "edit") => {
        if (isReturnInvoice(invoice)) {
            openReturnInvoice(invoice);
            return;
        }

        const isManualInvoice = getInvoiceOrigin(invoice) === "manual";
        const manualInvoiceId =
            invoice?._id || invoice?.id || invoice?.invoiceId || invoice?.invoice_id;

        if (isManualInvoice) {
            if (!manualInvoiceId) {
                appAlert(financeText("Manual Invoice ID not found!"));
                return;
            }

            if (mode === "view") {
                navigate(`/invoice/finance/jobs/${manualInvoiceId}`, {
                    state: {
                        job: invoice,
                        financeJobId: manualInvoiceId,
                        invoiceOrigin: "manual",
                    },
                });
            } else {
                navigate(`/invoice/finance/manual-invoices/${manualInvoiceId}/edit`, {
                    state: {
                        invoice,
                        invoiceId: manualInvoiceId,
                        editMode: true,
                    },
                });
            }

            return;
        }

        const recordIds = [
            invoice?._id,
            invoice?.id,
            invoice?.invoiceId,
            invoice?.invoice_id,
            invoice?.financeJobId,
            invoice?.finance_job_id,
            invoice?.jobFinanceId,
            invoice?.job_finance_id,
        ].filter(Boolean);
        const uniqueIds = [...new Set(recordIds.map(String))];
        let resolvedInvoice = invoice;

        for (const recordId of uniqueIds) {
            const detailPaths = [
                `/finance/manual-invoices/${recordId}`,
                `/invoice/${recordId}`,
                `/finance/customer-invoices/${recordId}`,
                `/finance/jobs/${recordId}`,
            ];

            for (const path of detailPaths) {
                try {
                    const response = await fetch(`${API_URL}${path}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) continue;

                    const payload = await response.json();
                    const detail =
                        payload?.data?.invoice ||
                        payload?.data?.job ||
                        payload?.data?.data ||
                        payload?.invoice ||
                        payload?.job ||
                        payload?.data ||
                        payload;

                    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
                        resolvedInvoice = {
                            ...invoice,
                            ...detail,
                            invoiceItems:
                                detail?.invoiceItems ||
                                detail?.invoice_items ||
                                invoice?.invoiceItems ||
                                invoice?.invoice_items ||
                                [],
                        };
                        break;
                    }
                } catch (error) {
                    console.warn(`${path} invoice detail lookup failed`, error);
                }
            }

            if (resolvedInvoice !== invoice) break;
        }

        const shipmentId =
            resolvedInvoice?.shipmentId ||
            resolvedInvoice?.shipment_id ||
            resolvedInvoice?._id ||
            resolvedInvoice?.id;

        if (!shipmentId) {
            appAlert(financeText("Shipment ID not found!"));
            return;
        }

        const sellingCurrency = getCurrency(resolvedInvoice);
        const costCurrency = getCostCurrency(resolvedInvoice);
        const sourceItems =
            [
                resolvedInvoice?.invoiceItems,
                resolvedInvoice?.invoice_items,
                resolvedInvoice?.cargoDetails,
                resolvedInvoice?.cargo_details,
            ].find(Array.isArray) || [];
        const normalizedInvoiceItems = sourceItems.map((item) => {
            const rawItem = item?.attributes || item;
            const unitQuantity =
                firstDefinedNumber(
                    rawItem?.unitQuantity,
                    rawItem?.unit_quantity,
                    rawItem?.quantity,
                    rawItem?.qty,
                ) || 1;
            const explicitUnitPrice = firstDefinedNumber(
                rawItem?.unitPrice,
                rawItem?.unit_price,
                rawItem?.salesPrice,
                rawItem?.price,
            );
            const explicitSellingRate = firstDefinedNumber(
                rawItem?.sellingRate,
                rawItem?.selling_rate,
                rawItem?.salesAmount,
            );
            const amountBasedUnitPrice =
                firstDefinedNumber(rawItem?.amount) / unitQuantity;
            const unitPrice =
                explicitUnitPrice > 0
                    ? explicitUnitPrice
                    : explicitSellingRate > 0
                        ? explicitSellingRate
                        : amountBasedUnitPrice;

            return {
                ...rawItem,
                commodity:
                    rawItem?.commodity ||
                    rawItem?.invoiceItem ||
                    rawItem?.invoice_item ||
                    rawItem?.label ||
                    "",
                description: rawItem?.description || "",
                totalCost: firstDefinedNumber(
                    rawItem?.totalCost,
                    rawItem?.total_cost,
                    rawItem?.cost,
                    rawItem?.unitCost,
                    rawItem?.unit_cost,
                ),
                costCurrency: normalizeCurrencyCode(
                    rawItem?.costCurrency || rawItem?.cost_currency || costCurrency,
                ),
                unitPrice,
                sellingRate: unitPrice,
                sellingRateCurrency: normalizeCurrencyCode(
                    rawItem?.sellingRateCurrency ||
                    rawItem?.selling_rate_currency ||
                    rawItem?.currency ||
                    sellingCurrency,
                ),
                unitQuantity,
                amount: unitPrice * unitQuantity,
            };
        });
        const invoiceItems = normalizedInvoiceItems.length
            ? normalizedInvoiceItems
            : [
                {
                    commodity: "Freight Charge",
                    description: "Shipment freight service",
                    totalCost: getTotalCost(resolvedInvoice),
                    costCurrency,
                    unitPrice: getInvoiceAmount(resolvedInvoice),
                    sellingRate: getInvoiceAmount(resolvedInvoice),
                    sellingRateCurrency: sellingCurrency,
                    unitQuantity: 1,
                    amount: getInvoiceAmount(resolvedInvoice),
                },
            ];
        const convertedFinancials = getConvertedFinancials(
            resolvedInvoice,
            sellingCurrency,
        );

        navigate("/create-invoice", {
            state: {
                openInvoiceModal: true,
                viewMode: mode === "view",
                editMode: mode === "edit",
                invoiceId:
                    resolvedInvoice?._id ||
                    resolvedInvoice?.id ||
                    resolvedInvoice?.invoiceId ||
                    resolvedInvoice?.invoice_id ||
                    null,
                financeJobId:
                    resolvedInvoice?.financeJobId ||
                    resolvedInvoice?.finance_job_id ||
                    resolvedInvoice?.jobFinanceId ||
                    resolvedInvoice?.job_finance_id ||
                    null,
                shipments: [
                    {
                        ...resolvedInvoice,
                        _id: shipmentId,
                        shipmentId,
                        shipmentNo: String(
                            resolvedInvoice?.shipmentNo || resolvedInvoice?.jobNo || "",
                        )
                            .replace("OHFL", "")
                            .replace("RHFL", "")
                            .replace("AHFL", ""),
                        customerName: getCustomerName(resolvedInvoice),
                        customer: getCustomerName(resolvedInvoice),
                        email: resolvedInvoice?.email || "",
                        invoiceItems,
                        cargoDetails: invoiceItems,
                        sellingRateCurrency: sellingCurrency,
                        costCurrency,
                        totalCostCurrency: costCurrency,
                        invoiceSalesAmount: getInvoiceAmount(resolvedInvoice),
                        salesAmount: getInvoiceAmount(resolvedInvoice),
                        sellingRate: getInvoiceAmount(resolvedInvoice),
                        totalSellingRate: getInvoiceAmount(resolvedInvoice),
                        totalAmount: getInvoiceAmount(resolvedInvoice),
                        customerInvoiceAmount: getInvoiceAmount(resolvedInvoice),
                        actualRevenueTotal: getInvoiceAmount(resolvedInvoice),
                        expectedRevenueTotal: getInvoiceAmount(resolvedInvoice),

                        operationalCost: getOperationalCost(resolvedInvoice),
                        buyingRate: getBuyingRate(resolvedInvoice),
                        actualCostTotal: getTotalCost(resolvedInvoice),
                        expectedCostTotal: getTotalCost(resolvedInvoice),
                        totalCost: getTotalCost(resolvedInvoice),
                        totalCostIncludesBuyingRate: true,

                        actualProfit: convertedFinancials.profit ?? 0,
                        expectedProfit: convertedFinancials.profit ?? 0,
                        actualMargin: convertedFinancials.margin ?? 0,

                        sourceType: getTransportMode(resolvedInvoice),
                        currency: sellingCurrency,
                    },
                ],
            },
        });
    };

    const openPdf = (invoice) => {
        const pdfUrl =
            invoice?.pdfUrl ||
            invoice?.pdf_url ||
            invoice?.uploadedFileUrl ||
            invoice?.uploaded_file_url;

        if (!pdfUrl) {
            appAlert(financeText("PDF not found!"));
            return;
        }

        window.open(pdfUrl, "_blank", "noopener,noreferrer");
    };

    const openCollection = (invoice) => {
        if (isReturnInvoice(invoice)) return;

        navigate("/invoice/finance/collections", {
            state: {
                invoice,
                openCollectionModal: true,
            },
        });
    };

    const markAsSent = async (invoice) => {
        if (isReturnInvoice(invoice)) return;

        const id = invoice?._id || invoice?.id;

        if (!id) {
            appAlert(financeText("Invoice ID not found!"));
            return;
        }

        try {
            const sentAt = new Date().toISOString();

            const response = await fetch(`${API_URL}/invoice/${id}`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    invoiceStatus: "Sent",
                    sentAt,
                }),
            });

            if (!response.ok) {
                throw new Error("Mark as sent failed");
            }

            setInvoices((previous) =>
                previous.map((item) =>
                    (item?._id || item?.id) === id
                        ? {
                            ...item,
                            invoiceStatus: "Sent",
                            sentAt,
                        }
                        : item,
                ),
            );
        } catch (error) {
            console.error("Mark as sent error:", error);
            appAlert(
                financeText(
                    "Invoice could not be marked as sent. Backend PATCH /invoice/:id should be checked."
                ),
            );
        }
    };

    const resetFilters = () => {
        setSearch("");
        setInvoiceStatus("all");
        setCollectionStatus("all");
        setCreditRisk("all");
        setDueStatus("all");
        setStartDate(`${currentYear}-01-01`);
        setEndDate(todayValue);
        setSelectedParty(null);
        setTransportMode("all");
        setCurrencyFilter("all");
        setInvoiceOrigin("all");
        setDocumentType("all");
        setReturnStatus("all");
        setGeographicScope("all");
        setEdmFilter("all");
        setEdmProfile("all");
    };

    const exportCsv = () => {
        const headers = [
            "Shipment",
            "Invoice No",
            "Document Type",
            "Original Invoice No",
            "Customer",
            "Supplier",
            "Geographic Scope",
            "Transport Mode",
            "Invoice Origin",
            "Selling Currency",
            "Cost Currency",
            "Reporting Currency",
            "Email",
            "Invoice Date",
            "Due Date",
            "Amount",
            "Operational Cost",
            "Buying Rate",
            "Total Cost",
            "Profit",
            "Margin",
            "Paid",
            "Balance",
            "Invoice Status",
            "Return Type",
            "Return Status",
            "Return Reason",
            "Return Amount",
            "Collection Status",
            "Credit Risk",
            "Risk Reason",
            "Provision Amount",
            "Risk Notes",
        ];

        const rows = filteredInvoices.map((invoice) => {
            const financials = getConvertedFinancials(invoice);

            return [
                getShipmentNo(invoice),
                getInvoiceNo(invoice),
                isReturnInvoice(invoice) ? "Return Invoice" : "Invoice",
                getOriginalInvoiceNo(invoice),
                getCustomerName(invoice),
                getSupplierName(invoice),
                getGeographicLabel(invoice, partyCountryLookup),
                MODE_CONFIG[getTransportMode(invoice)]?.label ||
                getTransportMode(invoice),
                getInvoiceOrigin(invoice) === "manual" ? "Manual" : "TMS",
                getCurrency(invoice),
                getCostCurrency(invoice),
                reportingCurrency,
                invoice?.email || "",
                formatDate(getValue(invoice, "invoiceDate", "invoice_date")),
                formatDate(getValue(invoice, "dueDate", "due_date")),
                convertCurrency(
                    getDisplayInvoiceAmount(invoice),
                    getCurrency(invoice),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getOperationalCost(invoice),
                    getCostCurrency(invoice),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getBuyingRate(invoice),
                    getCostCurrency(invoice),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                financials.cost ?? "Rate unavailable",
                financials.profit ?? "Rate unavailable",
                financials.margin === null
                    ? "Rate unavailable"
                    : `${financials.margin.toFixed(2)}%`,
                convertCurrency(
                    getPaidAmount(invoice),
                    getCurrency(invoice),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getBalanceAmount(invoice),
                    getCurrency(invoice),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                getDocumentStatus(invoice),
                isReturnInvoice(invoice)
                    ? String(invoice?.returnType || invoice?.return_type || "-")
                    : "-",
                getReturnSummary(invoice).status,
                isReturnInvoice(invoice)
                    ? invoice?.returnReason || invoice?.return_reason || ""
                    : "",
                isReturnInvoice(invoice)
                    ? Math.abs(getRawInvoiceAmount(invoice))
                    : getReturnSummary(invoice).amount,
                getCollectionStatus(invoice),
                getCollectionRiskStyle(getCollectionRisk(invoice).status).label,
                getCollectionRisk(invoice).reason,
                getCollectionRisk(invoice).provisionAmount,
                getCollectionRisk(invoice).notes,
            ];
        });

        const csv = [headers, ...rows]
            .map((row) =>
                row
                    .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
                    .join(","),
            )
            .join("\n");

        const blob = new Blob([`\uFEFF${csv}`], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        const modeName = transportMode === "all" ? "all-modes" : transportMode;
        const scopeName =
            geographicScope === "all" ? "all-regions" : geographicScope.toLowerCase();
        link.setAttribute(
            "download",
            `customer-invoices-${scopeName}-${modeName}-${todayValue}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("SHIPMENT"),
                    accessor: "jobNo",
                    width: "14%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const product = getValue(
                            invoice,
                            "productType",
                            "product_type",
                            "TMS",
                        );
                        const service = getValue(
                            invoice,
                            "serviceType",
                            "service_type",
                            "General",
                        );

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.2} py={0.35}>
                                <MDBox
                                    width={38}
                                    height={38}
                                    borderRadius="11px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        background:
                                            "#EEF4FF",
                                        color: COLORS.blue,
                                        border: "1px solid #D1E0FF",
                                    }}
                                >
                                    <ReceiptLong sx={{ fontSize: "18px !important" }} />
                                </MDBox>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",
                                            color: COLORS.navy,
                                            display: "block",
                                            maxWidth: 145,
                                            "&:hover": {
                                                color: COLORS.blue,
                                            },
                                        }}
                                        onClick={() => openInvoiceEditor(invoice, "view")}
                                    >
                                        {getShipmentNo(invoice)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {product} · {service}
                                    </MDTypography>

                                    <Chip
                                        size="small"
                                        label={`${financeText((MODE_CONFIG[getTransportMode(invoice)] || MODE_CONFIG.other).label)} · ${getShipmentType(invoice)}`}
                                        sx={{
                                            mt: 0.55,
                                            height: 20,
                                            color: (
                                                MODE_CONFIG[getTransportMode(invoice)] ||
                                                MODE_CONFIG.other
                                            ).color,
                                            background: (
                                                MODE_CONFIG[getTransportMode(invoice)] ||
                                                MODE_CONFIG.other
                                            ).light,
                                            border: `1px solid ${(MODE_CONFIG[getTransportMode(invoice)] || MODE_CONFIG.other).border}`,
                                            fontSize: "9px",
                                            fontWeight: 800,
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("INVOICE"),
                    accessor: "invoiceNo",
                    width: "14%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const originalInvoiceNo = getOriginalInvoiceNo(invoice);

                        return (
                            <MDBox minWidth={135}>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {getInvoiceNo(invoice)}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {formatDate(
                                        invoice?.invoiceDate ||
                                        invoice?.invoice_date ||
                                        invoice?.sentAt ||
                                        invoice?.createdAt ||
                                        invoice?.created_at,
                                    )}
                                </MDTypography>

                                {isReturnInvoice(invoice) && originalInvoiceNo && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.purple, fontWeight: 700 }}
                                    >
                                        {financeText("Original")}: {originalInvoiceNo}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("DOCUMENT TYPE"),
                    accessor: "documentType",
                    width: "10%",
                    Cell: ({ row }) => {
                        const returnDocument = isReturnInvoice(row.original);

                        return (
                            <Chip
                                size="small"
                                label={financeText(returnDocument ? "Return Invoice" : "Invoice")}
                                sx={{
                                    color: returnDocument ? COLORS.purple : COLORS.navy,
                                    background: returnDocument ? COLORS.purpleLight : "#EEF2F6",
                                    border: `1px solid ${returnDocument ? "#D9D6FE" : "#D0D5DD"}`,
                                    fontWeight: 800,
                                }}
                            />
                        );
                    },
                },
                {
                    Header: financeText("ORIGIN"),
                    accessor: "invoiceOrigin",
                    width: "8%",
                    Cell: ({ row }) => {
                        const origin = getInvoiceOrigin(row.original);

                        return (
                            <Chip
                                size="small"
                                label={origin === "manual" ? financeText("Manual") : "TMS"}
                                sx={{
                                    color: origin === "manual" ? COLORS.purple : COLORS.blue,
                                    background:
                                        origin === "manual" ? COLORS.purpleLight : COLORS.blueLight,
                                    fontWeight: 800,
                                }}
                            />
                        );
                    },
                },
                {
                    Header: financeText("CUSTOMER"),
                    accessor: "customerName",
                    width: "16%",
                    Cell: ({ row }) => {
                        const name = getCustomerName(row.original);

                        return (
                            <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Avatar
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        background: "#0B1F3A",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        boxShadow: "none",
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        display="block"
                                        sx={{
                                            color: COLORS.text,
                                            maxWidth: 150,
                                        }}
                                    >
                                        {name}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{
                                            color: COLORS.subtext,
                                            maxWidth: 150,
                                        }}
                                    >
                                        {row.original?.email || financeText("No email")}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("DUE DATE"),
                    accessor: "dueDate",
                    width: "11%",
                    Cell: ({ row }) => {
                        if (isReturnInvoice(row.original)) {
                            return (
                                <Chip
                                    size="small"
                                    label={financeText("Not Applicable")}
                                    sx={{
                                        color: COLORS.subtext,
                                        background: "#F2F4F7",
                                        fontWeight: 700,
                                    }}
                                />
                            );
                        }

                        const date = getValue(row.original, "dueDate", "due_date");

                        const overdueDays = getOverdueDays(
                            date,
                            getValue(row.original, "collectionStatus", "collection_status"),
                        );

                        return (
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: overdueDays > 0 ? COLORS.red : COLORS.text,
                                    }}
                                >
                                    {formatDate(date)}
                                </MDTypography>

                                {overdueDays > 0 ? (
                                    <Chip
                                        size="small"
                                        label={`${overdueDays} ${financeText("days overdue")}`}
                                        sx={{
                                            mt: 0.5,
                                            height: 21,
                                            color: COLORS.red,
                                            background: COLORS.redLight,
                                            fontSize: "10px",
                                            fontWeight: 700,
                                        }}
                                    />
                                ) : (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.green }}
                                    >
                                        {financeText("Within payment term")}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("AMOUNT"),
                    accessor: "totalAmount",
                    align: "right",
                    width: "14%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const currency = getCurrency(invoice);
                        const returnDocument = isReturnInvoice(invoice);
                        // Show what the customer is billed (VAT included) when the
                        // invoice was cut with VAT; profit/margin stay ex-VAT.
                        const vatBilled = !returnDocument && hasVatIncluded(invoice);
                        const total = vatBilled
                            ? getGrossBilledAmount(invoice, getDisplayInvoiceAmount(invoice))
                            : getDisplayInvoiceAmount(invoice);
                        const paid = getPaidAmount(invoice);
                        const balance = getBalanceAmount(invoice);
                        const paidRate = total ? Math.min((paid / total) * 100, 100) : 0;

                        return (
                            <MDBox minWidth={125}>
                                <MDBox textAlign="right">
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{
                                            color: returnDocument ? COLORS.purple : COLORS.blue,
                                        }}
                                    >
                                        {formatCompact(total, currency)}
                                    </MDTypography>

                                    {vatBilled && (
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Incl. VAT")}{" "}
                                            {formatCurrency(getVatAmount(invoice), currency)}
                                        </MDTypography>
                                    )}

                                    {returnDocument ? (
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Receivable adjustment")}
                                        </MDTypography>
                                    ) : (
                                        <>
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                sx={{ color: COLORS.green }}
                                            >
                                                {financeText("Paid")} {formatCurrency(paid, currency)}
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                sx={{
                                                    color: balance > 0 ? COLORS.red : COLORS.green,
                                                }}
                                            >
                                                {financeText("Balance")} {formatCurrency(balance, currency)}
                                            </MDTypography>
                                        </>
                                    )}
                                </MDBox>

                                {!returnDocument && (
                                    <LinearProgress
                                        variant="determinate"
                                        value={paidRate}
                                        sx={{
                                            mt: 0.7,
                                            height: 5,
                                            borderRadius: "999px",
                                            backgroundColor: "#EEF2F6",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: "999px",
                                                background:
                                                    paidRate >= 100
                                                        ? COLORS.green
                                                        : paidRate >= 50
                                                            ? COLORS.amber
                                                            : COLORS.red,
                                            },
                                        }}
                                    />
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("COST"),
                    accessor: "totalCost",
                    align: "right",
                    width: "11%",
                    Cell: ({ row }) =>
                        isReturnInvoice(row.original) ? (
                            <Chip
                                size="small"
                                label={financeText("Not Applicable")}
                                sx={{
                                    color: COLORS.subtext,
                                    background: "#F2F4F7",
                                    fontWeight: 700,
                                }}
                            />
                        ) : (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.red }}
                                >
                                    {formatCompact(
                                        getTotalCost(row.original),
                                        getCostCurrency(row.original),
                                    )}
                                </MDTypography>
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Stored cost")}
                                </MDTypography>
                            </MDBox>
                        ),
                },
                {
                    Header: financeText("PROFITABILITY"),
                    accessor: "actualProfit",
                    align: "right",
                    width: "12%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const financials = getConvertedFinancials(invoice);
                        const profit = financials.profit;
                        const margin = financials.margin;
                        const ratesAvailable = profit !== null;
                        const positive = profit >= 0;

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: !ratesAvailable
                                            ? COLORS.amber
                                            : positive
                                                ? COLORS.green
                                                : COLORS.red,
                                    }}
                                >
                                    {ratesAvailable
                                        ? formatCompact(profit, reportingCurrency)
                                        : financeText("Rate unavailable")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color: positive ? COLORS.green : COLORS.red,
                                    }}
                                >
                                    {ratesAvailable
                                        ? `${margin.toFixed(1)}% ${financeText("margin")}`
                                        : financeText("Missing exchange rate")}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("INVOICE STATUS"),
                    accessor: "invoiceStatus",
                    width: "10%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip status={getDocumentStatus(row.original)} />
                    ),
                },
                {
                    Header: financeText("RETURN STATUS"),
                    accessor: "returnStatus",
                    width: "12%",
                    Cell: ({ row }) => {
                        const summary = getReturnSummary(row.original);
                        const style =
                            RETURN_STATUS_STYLES[summary.status] ||
                            RETURN_STATUS_STYLES["No Return"];

                        return (
                            <MDBox minWidth={125}>
                                <Chip
                                    size="small"
                                    label={financeText(summary.status)}
                                    sx={{
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                    }}
                                />
                                {summary.count > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.45}
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {summary.count} {financeText(summary.count === 1 ? "document" : "documents")}
                                        {summary.amount > 0
                                            ? ` · ${formatCompact(
                                                summary.amount,
                                                getCurrency(row.original),
                                            )}`
                                            : ""}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("COLLECTION"),
                    accessor: "collectionStatus",
                    width: "10%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip status={getCollectionStatus(row.original)} />
                    ),
                },
                {
                    Header: financeText("CREDIT RISK"),
                    accessor: "riskStatus",
                    width: "10%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const risk = getCollectionRisk(invoice);
                        const style = getCollectionRiskStyle(risk.status);

                        return (
                            <Tooltip
                                arrow
                                title={
                                    risk.reason
                                        ? `${financeText(style.label)}: ${risk.reason}`
                                        : financeText("Open Collections to manage this risk")
                                }
                            >
                                <Chip
                                    size="small"
                                    label={financeText(style.label)}
                                    onClick={() => openCollection(invoice)}
                                    sx={{
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                        cursor: isReturnInvoice(invoice) ? "default" : "pointer",
                                        "&:hover": {
                                            filter: isReturnInvoice(invoice)
                                                ? "none"
                                                : "brightness(.97)",
                                        },
                                    }}
                                />
                            </Tooltip>
                        );
                    },
                },
                {
                    Header: financeText("PAYMENT TERM"),
                    accessor: "paymentTerm",
                    width: "9%",
                    Cell: ({ row }) => (
                        <Chip
                            size="small"
                            label={
                                isReturnInvoice(row.original)
                                    ? "Not Applicable"
                                    : getValue(row.original, "paymentTerm", "payment_term", "-")
                            }
                            sx={{
                                color: COLORS.navy,
                                background: "#EEF2F6",
                                fontWeight: 700,
                                borderRadius: "8px",
                            }}
                        />
                    ),
                },
                {
                    Header: financeText("EDM STATUS"),
                    id: "edmStatus",
                    accessor: (row) => getEdmState(row),
                    width: "11%",
                    Cell: ({ row }) => <EdmStatusPill invoice={row.original} />,
                },
                {
                    Header: financeText("PDF"),
                    accessor: "pdfUrl",
                    align: "center",
                    width: "6%",
                    Cell: ({ row }) => {
                        const hasPdf =
                            row.original?.pdfUrl ||
                            row.original?.pdf_url ||
                            row.original?.uploadedFileUrl ||
                            row.original?.uploaded_file_url;

                        return hasPdf ? (
                            <Tooltip title={financeText("View Invoice PDF")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => openPdf(row.original)}
                                    sx={{
                                        color: COLORS.red,
                                        background: COLORS.redLight,
                                        border: "1px solid #FECDCA",
                                        "&:hover": {
                                            background: "#FEE4E2",
                                        },
                                    }}
                                >
                                    <PictureAsPdf sx={{ fontSize: "17px !important" }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Chip
                                size="small"
                                label={financeText("No PDF")}
                                sx={{
                                    color: COLORS.subtext,
                                    background: "#F2F4F7",
                                    fontWeight: 700,
                                }}
                            />
                        );
                    },
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    align: "right",
                    width: "12%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const returnDocument = isReturnInvoice(invoice);
                        const hasLinkedReturns =
                            !returnDocument && getReturnSummary(invoice).count > 0;

                        return (
                            <MDBox display="flex" justifyContent="flex-end" gap={0.5}>
                                <Tooltip title={financeText("View Invoice")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => openInvoiceEditor(invoice, "view")}
                                        sx={{
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            border: "1px solid #B2DDFF",
                                            "&:hover": {
                                                background: "#D1E9FF",
                                            },
                                        }}
                                    >
                                        <Visibility sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>

                                {returnDocument && (
                                    <Tooltip title={financeText("Open Original Invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openOriginalInvoice(invoice)}
                                            sx={{
                                                color: COLORS.navy,
                                                background: "#EEF2F6",
                                                border: "1px solid #D0D5DD",
                                                "&:hover": {
                                                    background: "#E4E7EC",
                                                },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {hasLinkedReturns && (
                                    <Tooltip title={financeText("Open Latest Return Invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openLatestReturnInvoice(invoice)}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                border: "1px solid #D9D6FE",
                                                "&:hover": {
                                                    background: "#E9E7FD",
                                                },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {!returnDocument && (
                                    <Tooltip title={financeText("Edit Invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openInvoiceEditor(invoice)}
                                            sx={{
                                                color: COLORS.amber,
                                                background: COLORS.amberLight,
                                                border: "1px solid #FEDF89",
                                                "&:hover": {
                                                    background: "#FEF0C7",
                                                },
                                            }}
                                        >
                                            <Edit sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {!returnDocument && (
                                    <Tooltip title={financeText("Mark as Sent")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => markAsSent(invoice)}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                border: "1px solid #D9D6FE",
                                                "&:hover": {
                                                    background: "#E9E7FD",
                                                },
                                            }}
                                        >
                                            <MarkEmailRead sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {!returnDocument && (
                                    <Tooltip title={financeText("Add collection")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openCollection(invoice)}
                                            sx={{
                                                color: COLORS.green,
                                                background: COLORS.greenLight,
                                                border: "1px solid #ABEFC6",
                                                "&:hover": {
                                                    background: "#D1FADF",
                                                },
                                            }}
                                        >
                                            <Paid sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </MDBox>
                        );
                    },
                },
            ],
            rows: filteredInvoices,
        }),
        [filteredInvoices, getConvertedFinancials, reportingCurrency, financeLanguage],
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{ background: "rgba(255,255,255,.12)" }}
                                    >
                                        <WorkspacePremium sx={{ color: "#FFFFFF", fontSize: 20 }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE RECEIVABLES CENTER")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color: "#FFFFFF",
                                        lineHeight: 1.12,
                                    }}
                                >
                                    {financeText("Customer Invoices")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        maxWidth: 720,
                                    }}
                                >
                                    {financeText(
                                        "Manage invoices, return invoices, delivery, maturity dates, collections, overdue exposure and customer balances from one banking-grade workspace."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={<CheckCircle sx={{ color: "#75E0A7 !important" }} />}
                                        label={`${filteredInvoices.length} ${financeText("document records")}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth sx={{ color: "#B2DDFF !important" }} />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: "rgba(255,255,255,.72)" }}
                                        >
                                            {financeText("OUTSTANDING RECEIVABLES")}
                                        </MDTypography>

                                        <WarningAmber sx={{ color: "#FEDF89" }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{ color: "#FFFFFF" }}
                                    >
                                        {formatCompact(stats.balanceAmount, reportingCurrency)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: "rgba(255,255,255,.68)" }}
                                    >
                                        {financeText("Collection rate")}{" "}
                                        {stats.collectionRate.toFixed(2)}%{" "}
                                        {financeText("across filtered invoices")}
                                    </MDTypography>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "en" },
                                                    })
                                                }
                                                title="English"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "en"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "en"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "en"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/gb.svg"
                                                    alt="English"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    EN
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "tr" },
                                                    })
                                                }
                                                title="Türkçe"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "tr"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "tr"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "tr"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/tr.svg"
                                                    alt="Türkçe"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    TR
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={() =>
                                                navigate("/invoice/finance/manual-invoices/new")
                                            }
                                            sx={{
                                                background: "#FFFFFF !important",
                                                color: `${COLORS.navy} !important`,
                                                flex: 1,
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Add sx={{ mr: 0.5 }} />
                                            {financeText("Create Manual Invoice")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh Invoices")}>
                                            <IconButton
                                                onClick={fetchInvoices}
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Card sx={{ ...cardSx, mb: 2.5 }}>
                    <MDBox p={{ xs: 2, md: 2.5 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Start Date")}
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("End Date")}
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Customer / Supplier")}
                                    value={
                                        selectedParty
                                            ? `${selectedParty.type}-${selectedParty.name}`
                                            : "all"
                                    }
                                    onChange={(event) => {
                                        if (event.target.value === "all") {
                                            setSelectedParty(null);
                                            return;
                                        }

                                        setSelectedParty(
                                            partyOptions.find(
                                                (option) =>
                                                    `${option.type}-${option.name}` ===
                                                    event.target.value,
                                            ) || null,
                                        );
                                    }}
                                >
                                    <MenuItem value="all">{financeText("All Customers / Suppliers")}</MenuItem>
                                    {partyOptions.map((option) => {
                                        const value = `${option.type}-${option.name}`;
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {financeText(option.label)}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Geographic Scope")}
                                    value={geographicScope}
                                    onChange={(event) => setGeographicScope(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Geographic Scopes")}</MenuItem>
                                    <MenuItem value="DOMESTIC">{financeText("Domestic")}</MenuItem>
                                    <MenuItem value="INTERNATIONAL">{financeText("International")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Transport Mode")}
                                    value={transportMode}
                                    onChange={(event) => setTransportMode(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Transport Modes")}</MenuItem>
                                    {transportModeOptions.map((mode) => (
                                        <MenuItem key={mode} value={mode}>
                                            {financeText(MODE_CONFIG[mode]?.label || mode)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice Origin")}
                                    value={invoiceOrigin}
                                    onChange={(event) => setInvoiceOrigin(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Origins")}</MenuItem>
                                    <MenuItem value="tms">TMS</MenuItem>
                                    <MenuItem value="manual">{financeText("Manual")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={currencyFilter}
                                    onChange={(event) => setCurrencyFilter(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Currencies")}</MenuItem>
                                    {currencyOptions.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Separate from the "Currency" record filter above:
                                that one narrows which invoices are listed, this
                                one only chooses the currency the aggregated
                                totals/KPIs are converted into for display. */}
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Display Currency")}
                                    value={selectedCurrency}
                                    onChange={(event) => setSelectedCurrency(event.target.value)}
                                >
                                    {CURRENCY_OPTIONS.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Document Type")}
                                    value={documentType}
                                    onChange={(event) => setDocumentType(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Document Types")}</MenuItem>
                                    <MenuItem value="invoice">{financeText("Invoice")}</MenuItem>
                                    <MenuItem value="return_invoice">{financeText("Return Invoice")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Return Status")}
                                    value={returnStatus}
                                    onChange={(event) => setReturnStatus(event.target.value)}
                                >
                                    {returnStatusOptions.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Credit Risk")}
                                    value={creditRisk}
                                    onChange={(event) => setCreditRisk(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Credit Risks")}</MenuItem>
                                    {COLLECTION_RISK_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {financeText(option.label)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {!loading && (
                    <MDBox mb={SPACE.sectionGap}>
                        <EdmOverview
                            invoices={baseFilteredInvoices}
                            activeState={edmFilter}
                            onSelect={setEdmFilter}
                            formatAmount={formatEdmAmount}
                        />
                    </MDBox>
                )}

                {loading ? (
                    <Grid container spacing={SPACE.rowGap}>
                        <Grid item xs={12} lg={7}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {/* Same hero + secondary-tiles pattern as the Finance
                            Dashboard, from the shared FinanceUiKit. */}

                        <Grid container spacing={SPACE.rowGap} mt={0.5}>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Documents")}
                                    value={formatNumber(stats.invoiceCount)}
                                    subtitle={financeText("Invoices and return invoices")}
                                    icon={<ReceiptLong />}
                                    tone="navy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Collection Rate")}
                                    value={`${stats.collectionRate.toFixed(1)}%`}
                                    subtitle={financeText("Paid versus invoiced")}
                                    icon={<CheckCircle />}
                                    tone="green"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Buying Rate")}
                                    value={formatCompact(stats.buyingRate, reportingCurrency)}
                                    subtitle={financeText("Included purchase cost")}
                                    icon={<Paid />}
                                    tone="amber"
                                />
                            </Grid>
                        </Grid>



                        <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDBox display="flex" alignItems="center" gap={1}>
                                            <MDBox
                                                width={38}
                                                height={38}
                                                borderRadius="11px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    border: "1px solid #B2DDFF",
                                                }}
                                            >
                                                <FilterAlt sx={{ fontSize: 19 }} />
                                            </MDBox>

                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Invoice Filters")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(
                                                        "Search and segment invoices and return invoices by number, customer, document type and status."
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={fetchInvoices}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <Refresh sx={{ mr: 0.5 }} />
                                            {financeText("Refresh")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={resetFilters}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <RestartAlt sx={{ mr: 0.5 }} />
                                            {financeText("Reset")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            size="small"
                                            onClick={exportCsv}
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Download sx={{ mr: 0.5 }} />
                                            {financeText("Export CSV")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={() => navigate("/invoice/finance/jobs")}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("Finance Jobs")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("Shipment, invoice, customer...")}
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search
                                                            fontSize="small"
                                                            sx={{ color: COLORS.subtext }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Invoice Status")}
                                            value={invoiceStatus}
                                            onChange={(event) => setInvoiceStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.invoice.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Invoice Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Collection Status")}
                                            value={collectionStatus}
                                            onChange={(event) =>
                                                setCollectionStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.collection.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Collection Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Due Status")}
                                            value={dueStatus}
                                            onChange={(event) => setDueStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Due Statuses")}</MenuItem>
                                            <MenuItem value="overdue">{financeText("Overdue")}</MenuItem>
                                            <MenuItem value="notDue">{financeText("Not Due")}</MenuItem>
                                            <MenuItem value="noDueDate">{financeText("No Due Date")}</MenuItem>
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Document Type")}
                                            value={documentType}
                                            onChange={(event) => setDocumentType(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Document Types")}</MenuItem>
                                            <MenuItem value="invoice">{financeText("Invoice")}</MenuItem>
                                            <MenuItem value="return_invoice">{financeText("Return Invoice")}</MenuItem>
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Return Status")}
                                            value={returnStatus}
                                            onChange={(event) => setReturnStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {returnStatusOptions.map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <EdmStatusSelect value={edmFilter} onChange={setEdmFilter} />
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <EdmProfileSelect value={edmProfile} onChange={setEdmProfile} />
                                    </Grid>
                                </Grid>

                                <MDBox
                                    mt={2.2}
                                    p={1.5}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1}
                                    sx={{
                                        background: "#F9FAFB",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Showing")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {filteredInvoices.length}
                                        </strong>{" "}
                                        {financeText("of")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {invoices.length}
                                        </strong>{" "}
                                        {financeText("invoice documents")}
                                    </MDTypography>

                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            size="small"
                                            label={`${financeText("TOTAL")} ${formatCompact(stats.billedTotal, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.blue,
                                                background: COLORS.blueLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Cost")} ${formatCompact(stats.totalCost, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Profit")} ${formatCompact(stats.profit, reportingCurrency)}`}
                                            sx={{
                                                color: stats.profit >= 0 ? COLORS.green : COLORS.red,
                                                background:
                                                    stats.profit >= 0
                                                        ? COLORS.greenLight
                                                        : COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Outstanding")} ${formatCompact(
                                                stats.balanceAmount,
                                                reportingCurrency,
                                            )}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>

                                <Divider sx={{ my: 2.5 }} />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: "10px",
                                            overflowX: "auto",
                                            boxShadow: "none",
                                        },
                                        "& table": {
                                            minWidth: 1900,
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                        },
                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },
                                        "& table thead th": {
                                            background: "transparent",
                                            color: "#475467 !important",
                                            fontSize: "10px !important",
                                            fontWeight: "800 !important",
                                            letterSpacing: ".085em",
                                            paddingTop: "15px !important",
                                            paddingBottom: "15px !important",
                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },
                                        "& table tbody td": {
                                            paddingTop: "14px !important",
                                            paddingBottom: "14px !important",
                                            borderBottom: "1px solid #F0F2F5 !important",
                                            verticalAlign: "middle",
                                        },
                                        "& table tbody tr": {
                                            background: "#FFFFFF",
                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },
                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },
                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",
                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },
                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },
                                        "& .MuiTablePagination-root": {
                                            borderTop: `1px solid ${COLORS.border}`,
                                            background: "#FCFCFD",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage
                                        showTotalEntries
                                        isSorted
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="customerInvoices"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={SPACE.rowGap}>
                            <Grid item xs={12} lg={7}>
                                <FinanceHeroStat
                                    label={financeText("Gross Profit")}
                                    value={formatCompact(stats.profit, reportingCurrency)}
                                    tone={stats.profit >= 0 ? "green" : "red"}
                                    icon={<ArrowUpward />}
                                    meta={`${financeText("Margin")} ${stats.margin.toFixed(2)}%`}
                                    context={[
                                        { label: financeText("Invoice Total"), value: formatCompact(stats.billedTotal, reportingCurrency) },
                                        { label: financeText("Collected"), value: formatCompact(stats.paidAmount, reportingCurrency) },
                                        { label: financeText("Total Cost"), value: formatCompact(stats.totalCost, reportingCurrency) },
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <FinanceCard>
                                    <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%">
                                        <FinanceSectionHeader
                                            title={getFinanceLanguage() === "tr" ? "Dikkat gerektiren" : "Needs attention"}
                                        />
                                        <MDBox display="flex" flexDirection="column" gap={1.25}>
                                            {[
                                                { label: financeText("Outstanding"), value: formatCompact(stats.balanceAmount, reportingCurrency), sub: financeText("Open customer balance"), tone: "amber" },
                                                { label: financeText("High-Risk Exposure"), value: formatCompact(stats.highRiskExposure, reportingCurrency), sub: financeText("Doubtful, bad debt and written-off"), tone: "red" },
                                                { label: financeText("Overdue"), value: formatNumber(stats.overdueCount), sub: `${stats.draftCount} ${financeText("draft invoices")}`, tone: "red" },
                                            ].map((row) => (
                                                <MDBox
                                                    key={row.label}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    px={1.75}
                                                    py={1.25}
                                                    borderRadius={SPACE.radiusTile}
                                                    sx={{ background: COLORS.muted }}
                                                >
                                                    <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                                        <MDBox width={8} height={8} borderRadius="50%" flexShrink={0} sx={{ background: COLORS[row.tone] }} />
                                                        <MDBox minWidth={0}>
                                                            <MDTypography variant="button" fontWeight="medium" sx={{ color: COLORS.text, display: "block" }}>
                                                                {row.label}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                                                {row.sub}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS[row.tone], flexShrink: 0 }}>
                                                        {row.value}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                </FinanceCard>
                            </Grid>
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            {Object.entries(transportSummary).map(([mode, data]) => (
                                <Grid item xs={12} md={4} key={mode}>
                                    <TransportInvoiceCard
                                        mode={mode}
                                        data={data}
                                        totalInvoices={filteredInvoices.length}
                                        currency={reportingCurrency}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            <Grid item xs={12} lg={7}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.5}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Invoice Value by Transport Mode")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(
                                                        "Customer billing and outstanding receivables across Ocean, Road and Air"
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    border: "1px solid #B2DDFF",
                                                }}
                                            >
                                                <ReceiptLong />
                                            </MDBox>
                                        </MDBox>
                                        <ModeReceivablesChart
                                            summary={transportSummary}
                                            currency={reportingCurrency}
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.2}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Shipment Type Mix")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Most frequently invoiced logistics services")}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.purple,
                                                    background: COLORS.purpleLight,
                                                    border: "1px solid #D9D6FE",
                                                }}
                                            >
                                                <Inventory2 />
                                            </MDBox>
                                        </MDBox>

                                        {shipmentTypeSummary.length ? (
                                            shipmentTypeSummary.map(([type, count], index) => {
                                                const max = shipmentTypeSummary[0]?.[1] || 1;
                                                return (
                                                    <MDBox key={type} mb={1.55}>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            mb={0.55}
                                                        >
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {index + 1}. {type}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.purple }}
                                                            >
                                                                {count} {financeText("invoices")}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(count / max) * 100}
                                                            sx={{
                                                                height: 6,
                                                                borderRadius: "999px",
                                                                backgroundColor: "#EEF2F6",
                                                                "& .MuiLinearProgress-bar": {
                                                                    borderRadius: "999px",
                                                                    background:
                                                                        index === 0 ? COLORS.purple : COLORS.blue,
                                                                },
                                                            }}
                                                        />
                                                    </MDBox>
                                                );
                                            })
                                        ) : (
                                            <MDBox py={5} textAlign="center">
                                                <MDTypography
                                                    variant="body2"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("No shipment type information is available.")}
                                                </MDTypography>
                                            </MDBox>
                                        )}
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Footer />
        </DashboardLayout>
    );
};

export default CustomerInvoices;